import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar /*, Doughnut */ } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartArea, /* faChartPie, */ faListOl, faBalanceScale } from '@fortawesome/free-solid-svg-icons';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Currency tick formatter
const formatShortRupee = (value) => {
  if (value === 0 || value === '0') return '₹0';
  const val = Number(value);
  if (isNaN(val)) return '₹0';
  const absVal = Math.abs(val);
  const sign = val < 0 ? '-' : '';

  if (absVal >= 10000000) {
    const cr = val / 10000000;
    return `${sign}₹${cr.toFixed(1).replace(/\.0$/, '')} Cr`;
  } else if (absVal >= 100000) {
    const lk = val / 100000;
    return `${sign}₹${lk.toFixed(1).replace(/\.0$/, '')} L`;
  } else if (absVal >= 1000) {
    const k = val / 1000;
    return `${sign}₹${k.toFixed(1).replace(/\.0$/, '')} K`;
  }
  return `₹${val.toLocaleString('en-IN')}`;
};

const AdsReportCharts = ({ charts = {}, loading = false, viewMode = 'platform' }) => {
  if (loading) {
    return (
      <>
        {/* Row 1 Skeleton: Full Width Timeline Chart */}
        <div className="row mb-4">
          <div className="col-lg-12 mb-4">
            <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
              <div className="panel-heading">
                <h4 className="panel-title">
                  <div className="skeleton-text" style={{ width: '220px', height: '14px' }}></div>
                </h4>
              </div>
              <div className="panel-body p-4 d-flex justify-content-center align-items-center" style={{ height: '280px' }}>
                <div className="skeleton-text" style={{ height: '220px', width: '95%', borderRadius: '6px' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2 Skeleton: Top Campaigns Full Width */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
              <div className="panel-heading">
                <h4 className="panel-title">
                  <div className="skeleton-text" style={{ width: '180px', height: '14px' }}></div>
                </h4>
              </div>
              <div className="panel-body p-4 d-flex justify-content-center align-items-center" style={{ height: '280px' }}>
                <div className="skeleton-text" style={{ height: '220px', width: '95%', borderRadius: '6px' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3 Skeleton: Cost Per Lead Full Width */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="panel panel-default with-rounded-corner with-shadow h-100" style={{ marginBottom: 0 }}>
              <div className="panel-heading">
                <h4 className="panel-title">
                  <div className="skeleton-text" style={{ width: '180px', height: '14px' }}></div>
                </h4>
              </div>
              <div className="panel-body p-4 d-flex justify-content-center align-items-center" style={{ height: '280px' }}>
                <div className="skeleton-text" style={{ height: '220px', width: '95%', borderRadius: '6px' }}></div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const { trends = [], platforms = [], campaignGroups = [], topCampaigns = [] } = charts;
  const isCampaignView = viewMode === 'campaign';
  const groupItems = isCampaignView ? (campaignGroups || []) : platforms;

  // 1. Timeline Trends Chart (Spend vs Leads)
  const trendLabels = trends.map((t) => t.date || t.month);
  const trendCostData = trends.map((t) => t.cost);
  const trendLeadsData = trends.map((t) => t.leads);

  const trendChartData = {
    labels: trendLabels.length > 0 ? trendLabels : ['No Data'],
    datasets: [
      {
        type: 'bar',
        label: 'Ad Spend (₹)',
        data: trendCostData.length > 0 ? trendCostData : [0],
        backgroundColor: 'rgba(59, 130, 246, 0.75)',
        borderColor: '#2563eb',
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'yCost',
        order: 2,
      },
      {
        type: 'line',
        label: 'Leads',
        data: trendLeadsData.length > 0 ? trendLeadsData : [0],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2.5,
        tension: 0.3,
        fill: true,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: '#10b981',
        yAxisID: 'yLeads',
        order: 1,
      },
    ],
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 12, font: { size: 12 } } },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset.yAxisID === 'yCost') {
              return `Ad Spend: ₹${Number(context.raw).toLocaleString('en-IN')}`;
            }
            return `Leads: ${Number(context.raw).toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      yCost: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'Spend (₹)' },
        ticks: { callback: (v) => formatShortRupee(v) },
        grid: { color: '#f1f5f9' },
      },
      yLeads: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Leads' },
        grid: { display: false },
        ticks: { callback: (v) => Number(v).toLocaleString('en-IN') },
      },
    },
  };

  /*
  // 2. Spend Doughnut (Platform vs Campaign)
  const groupLabels = groupItems.map((g) => g.campaign_name || g.name || g.platform || 'Unmapped');
  const groupCostData = groupItems.map((g) => g.cost);
  const chartColors = ['#2563eb', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1', '#e11d48'];

  const doughnutData = {
    labels: groupLabels.length > 0 ? groupLabels : ['No Data'],
    datasets: [
      {
        data: groupCostData.length > 0 ? groupCostData : [1],
        backgroundColor: groupLabels.length > 0 ? chartColors.slice(0, groupLabels.length) : ['#e2e8f0'],
        borderWidth: 2,
        borderColor: '#ffffff',
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (context) => {
            const val = context.raw || 0;
            return `Spend: ₹${Number(val).toLocaleString('en-IN')}`;
          },
        },
      },
    },
    cutout: '65%',
  };
  */

  // 3. Top Campaigns Horizontal Bar Chart
  const campaignLabels = topCampaigns.map((c) => {
    const name = c.campaign_name || c.campaign || '';
    return name.length > 55 ? `${name.substring(0, 52)}...` : name;
  });
  const campaignSpendData = topCampaigns.map((c) => c.cost);

  const topCampaignsData = {
    labels: campaignLabels.length > 0 ? campaignLabels : ['No Campaigns'],
    datasets: [
      {
        label: 'Ad Spend (₹)',
        data: campaignSpendData.length > 0 ? campaignSpendData : [0],
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: '#6366f1',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const topCampaignsOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return topCampaigns[idx]?.campaign_name || topCampaigns[idx]?.campaign || '';
          },
          label: (ctx) => `Spend: ₹${Number(ctx.raw).toLocaleString('en-IN')}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { callback: (v) => formatShortRupee(v) },
        grid: { color: '#f1f5f9' },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11.5 } },
      },
    },
  };

  // 4. Cost Efficiency (CPL Comparison) - using campaign_name
  const cplLabels = groupItems.map((g) => {
    const name = isCampaignView
      ? (g.campaign_name || g.campaign || 'Unmapped')
      : (g.platform || 'Unmapped');
    return name.length > 50 ? `${name.substring(0, 47)}...` : name;
  });
  const cplValues = groupItems.map((g) => g.cpl);

  const cplChartData = {
    labels: cplLabels.length > 0 ? cplLabels : ['No Data'],
    datasets: [
      {
        label: 'Avg Cost Per Lead (CPL ₹)',
        data: cplValues.length > 0 ? cplValues : [0],
        backgroundColor: 'rgba(16, 185, 129, 0.75)',
        borderColor: '#059669',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const cplChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const idx = items[0].dataIndex;
            return isCampaignView
              ? (groupItems[idx]?.campaign_name || groupItems[idx]?.campaign || '')
              : (groupItems[idx]?.platform || '');
          },
          label: (ctx) => `CPL: ₹${Number(ctx.raw).toLocaleString('en-IN')}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { callback: (v) => formatShortRupee(v) },
        grid: { color: '#f1f5f9' },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11.5 } },
      },
    },
  };

  return (
    <>
      {/* Row 1: Full-Width Spend & Leads Timeline */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="panel panel-default with-rounded-corner with-shadow chartbox" style={{ marginBottom: 0 }}>
            <div className="panel-heading">
              <h4 className="panel-title" style={{ fontSize: '13.5px', fontWeight: '600', margin: 0 }}>
                <FontAwesomeIcon icon={faChartArea} style={{ color: '#3b82f6', marginRight: 6 }} />
                Spend & Leads Timeline (Daily / Monthly Trends)
              </h4>
            </div>
            <div className="panel-body" style={{ height: '300px', padding: '15px' }}>
              <Bar key="spend-timeline-chart" data={trendChartData} options={trendChartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Chart 2: Budget Split (Commented out as requested) */}
      {/*
      <div className="row mb-4">
        <div className="col-12">
          <div className="panel panel-default with-rounded-corner with-shadow chartbox h-100" style={{ marginBottom: 0 }}>
            <div className="panel-heading">
              <h4 className="panel-title" style={{ fontSize: '13.5px', fontWeight: '600', margin: 0 }}>
                <FontAwesomeIcon icon={faChartPie} style={{ color: '#8b5cf6', marginRight: 6 }} />
                {isCampaignView ? 'Campaign Budget Split' : 'Platform Budget Split'}
              </h4>
            </div>
            <div className="panel-body" style={{ height: '300px', padding: '15px' }}>
              <Doughnut data={doughnutData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>
      */}

      {/* Row 2: Top Campaigns by Spend (Full Width) */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="panel panel-default with-rounded-corner with-shadow chartbox" style={{ marginBottom: 0 }}>
            <div className="panel-heading">
              <h4 className="panel-title" style={{ fontSize: '13.5px', fontWeight: '600', margin: 0 }}>
                <FontAwesomeIcon icon={faListOl} style={{ color: '#6366f1', marginRight: 6 }} />
                Top Campaigns by Spend
              </h4>
            </div>
            <div className="panel-body" style={{ height: '340px', padding: '15px' }}>
              <Bar key="top-campaigns-chart" data={topCampaignsData} options={topCampaignsOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Cost per Lead (Full Width) */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="panel panel-default with-rounded-corner with-shadow chartbox" style={{ marginBottom: 0 }}>
            <div className="panel-heading">
              <h4 className="panel-title" style={{ fontSize: '13.5px', fontWeight: '600', margin: 0 }}>
                <FontAwesomeIcon icon={faBalanceScale} style={{ color: '#10b981', marginRight: 6 }} />
                {isCampaignView ? 'Cost Per Lead by Campaign' : 'Cost Per Lead by Platform'}
              </h4>
            </div>
            <div
              className="panel-body"
              style={{
                height: isCampaignView && cplLabels.length > 5 ? `${Math.min(500, Math.max(340, cplLabels.length * 32))}px` : '320px',
                padding: '15px',
              }}
            >
              <Bar key="cpl-comparison-chart" data={cplChartData} options={cplChartOptions} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdsReportCharts;
